import type {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridColDef,
} from "@mui/x-data-grid-premium";
import type { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";

const GRID_STATE_VERSION = 1;
const GRID_STATE_PREFIX = `portal-ksef:grid-state:v${GRID_STATE_VERSION}`;

type GridStatePayload = {
  version: number;
  savedAt: string;
  state: GridInitialStatePremium;
};

export type GridControlledModels = {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
};

export type PartialGridControlledModels = Partial<GridControlledModels>;

export function buildGridStateStorageKey(params: {
  table: string;
  companyId?: string;
  view?: string;
}) {
  return [
    GRID_STATE_PREFIX,
    params.table,
    params.companyId ?? "global",
    params.view ?? "default",
  ].join(":");
}

function canUseLocalStorage() {
  return typeof window !== "undefined" && !!window.localStorage;
}

function sanitizeGridState(
  state: GridInitialStatePremium
): GridInitialStatePremium {
  const cloned = JSON.parse(JSON.stringify(state ?? {}));

  delete cloned.rowSelection;
  delete cloned.cellSelection;

  // Pinowanie kolumn usunięte z UI.
  delete cloned.pinnedColumns;

  delete cloned.sidebar;
  delete cloned.preferencePanel;

  if (cloned.pagination) {
    delete cloned.pagination.rowCount;
    delete cloned.pagination.paginationMeta;
    delete cloned.pagination.meta;
  }

  // Kolumny zmienione na singleSelect — usuwamy stare filtry tekstowe
  const SINGLE_SELECT_FIELDS = new Set(["assignedUserEmail"]);
  const SINGLE_SELECT_OPERATORS = new Set(["is", "not", "isAnyOf"]);

  if (cloned.filter?.filterModel?.items) {
    cloned.filter.filterModel.items = cloned.filter.filterModel.items.filter(
      (item: any) => {
        if (SINGLE_SELECT_FIELDS.has(item.field)) {
          return SINGLE_SELECT_OPERATORS.has(item.operator);
        }
        return true;
      }
    );
  }

  return cloned;
}

export function readGridState(
  storageKey: string
): GridInitialStatePremium | undefined {
  if (!canUseLocalStorage()) return undefined;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;

    const payload = JSON.parse(raw) as GridStatePayload;

    if (payload?.version !== GRID_STATE_VERSION || !payload.state) {
      return undefined;
    }

    return sanitizeGridState(payload.state);
  } catch {
    return undefined;
  }
}

export function writeGridState(
  storageKey: string,
  state: GridInitialStatePremium
) {
  if (!canUseLocalStorage()) return;

  const payload: GridStatePayload = {
    version: GRID_STATE_VERSION,
    savedAt: new Date().toISOString(),
    state: sanitizeGridState(state),
  };

  window.localStorage.setItem(storageKey, JSON.stringify(payload));
}


export function writeGridControlledModels(
  storageKey: string,
  models: GridControlledModels
) {
  const currentState = readGridState(storageKey) ?? {};
  const nextState = mergeGridControlledModels(currentState, models);

  writeGridState(storageKey, nextState);
}

export function mergeGridControlledModels(
  state: GridInitialStatePremium,
  models?: PartialGridControlledModels
): GridInitialStatePremium {
  if (!models) {
    return state;
  }

  return {
    ...state,
    pagination: models.paginationModel
      ? {
          ...(state as any)?.pagination,
          paginationModel: models.paginationModel,
        }
      : (state as any)?.pagination,
    sorting: models.sortModel
      ? {
          ...(state as any)?.sorting,
          sortModel: models.sortModel,
        }
      : (state as any)?.sorting,
    filter: models.filterModel
      ? {
          ...(state as any)?.filter,
          filterModel: models.filterModel,
        }
      : (state as any)?.filter,
  };
}

export function omitGridControlledModels(
  state?: GridInitialStatePremium
): GridInitialStatePremium | undefined {
  if (!state) {
    return undefined;
  }

  const nextState: any = {
    ...state,
  };

  if (nextState.pagination) {
    const { paginationModel, ...pagination } = nextState.pagination;
    nextState.pagination = pagination;
  }

  if (nextState.sorting) {
    const { sortModel, ...sorting } = nextState.sorting;
    nextState.sorting = sorting;
  }

  if (nextState.filter) {
    const { filterModel, ...filter } = nextState.filter;
    nextState.filter = filter;
  }

  return nextState;
}

export function clearGridState(storageKey: string) {
  if (!canUseLocalStorage()) return;
  window.localStorage.removeItem(storageKey);
}

export function readGridControlledModels(
  storageKey: string,
  defaults: GridControlledModels
): GridControlledModels {
  const state: any = readGridState(storageKey);

  return {
    paginationModel:
      state?.pagination?.paginationModel ?? defaults.paginationModel,

    sortModel:
      state?.sorting?.sortModel ?? defaults.sortModel,

    filterModel:
      state?.filter?.filterModel ?? defaults.filterModel,
  };
}

export function applySavedColumnOrder<R = any>(
  columns: GridColDef<R>[],
  initialState?: GridInitialStatePremium
): GridColDef<R>[] {
  const orderedFields = (initialState as any)?.columns?.orderedFields as
    | string[]
    | undefined;

  if (!orderedFields?.length) {
    return columns;
  }

  const columnsByField = new Map(
    columns.map((column) => [column.field, column])
  );

  const reorderedColumns = orderedFields
    .filter((field) => columnsByField.has(field))
    .map((field) => columnsByField.get(field)!);

  const newColumns = columns.filter(
    (column) => !orderedFields.includes(column.field)
  );

  return [...reorderedColumns, ...newColumns];
}